import { callApi } from "./api";

export const DOC_GEN_TIMEOUT_SEC = 180;
export const DOC_GEN_FILE_POLL_MS = 5000;
export const DOC_GEN_TIMEOUT_MESSAGE = `Document generation timed out. Please try again after a few minutes. If the issue persists, contact the Support Team.`;
export const DOC_GEN_WORKER_DOWN_MESSAGE =
  "Word document generation service is not running. Please contact the Support Team.";

export function isDocGenWorkerDownError(message: string): boolean {
  const m = (message || "").toLowerCase();
  return (
    m === DOC_GEN_WORKER_DOWN_MESSAGE.toLowerCase() ||
    m.includes("reqrep_server.py") ||
    m.includes("generation service is not running") ||
    m.includes("generation service is unreachable")
  );
}

export async function isDocGenWorkerRunning(): Promise<boolean | null> {
  try {
    const res = await callApi(
      "GET",
      "template/tests/docgen_worker_status.php",
    );
    const data = res && res.data ? res.data : null;
    if (data && (Number(data.running) === 1 || data.running === true)) {
      return true;
    }
    if (data && (Number(data.running) === 0 || data.running === false)) {
      return false;
    }
    return null;
  } catch (e) {
    return null;
  }
}

export function failWithDocGenTimeout(onFailure: (message: string) => void) {
  isDocGenWorkerRunning().then((running) => {
    onFailure(
      running === false ? DOC_GEN_WORKER_DOWN_MESSAGE : DOC_GEN_TIMEOUT_MESSAGE,
    );
  });
}

export const DOC_GEN_WORKER_POLL_MS = 2000;
export const DOC_GEN_WORKER_POLL_MAX = 10;

export function watchDocGenWorkerUntilUp(onUp: () => void): () => void {
  let stopped = false;
  let attempts = 0;

  const tick = () => {
    if (stopped) {
      return;
    }
    attempts += 1;
    const attemptNo = attempts;
    if (attemptNo >= DOC_GEN_WORKER_POLL_MAX) {
      clearInterval(id);
    }
    isDocGenWorkerRunning().then((running) => {
      if (stopped) {
        return;
      }
      if (running === true) {
        stopped = true;
        clearInterval(id);
        onUp();
        return;
      }
      if (attemptNo >= DOC_GEN_WORKER_POLL_MAX) {
        stopped = true;
        clearInterval(id);
      }
    });
  };

  const id = setInterval(tick, DOC_GEN_WORKER_POLL_MS);
  tick();
  return () => {
    stopped = true;
    clearInterval(id);
  };
}

const storageKey = (fileId: string | number) => `docGenTimeout_${fileId}`;

export function readDocGenTimeout(fileId: string | number): string | null {
  try {
    const raw = sessionStorage.getItem(storageKey(fileId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && parsed.error
      ? String(parsed.error)
      : "Please try after sometime.";
  } catch (e) {
    return null;
  }
}

export function writeDocGenTimeout(fileId: string | number, error: string) {
  sessionStorage.setItem(
    storageKey(fileId),
    JSON.stringify({ error, at: Date.now() }),
  );
}

export function clearDocGenTimeout(fileId: string | number) {
  sessionStorage.removeItem(storageKey(fileId));
}

export function isDocGenQueued(response: any): boolean {
  return !!(
    response &&
    response.result !== false &&
    Number(response.status) === 130 &&
    response.data
  );
}

export function extractAxiosDocGenError(err: any): string {
  const data = err && err.response ? err.response.data : null;
  if (data && typeof data === "object") {
    if (data.message) {
      return String(data.message);
    }
    if (data.error) {
      return String(data.error);
    }
  }
  if (typeof data === "string") {
    const stripped = data.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (stripped) {
      return stripped.slice(0, 500);
    }
  }
  if (err && err.message) {
    return String(err.message);
  }
  return "Report generation request failed.";
}

export function isDocGenFailedResponse(response: any): boolean {
  if (!response) {
    return true;
  }
  if (response.result === false || response.success === false) {
    return true;
  }
  const status = Number(response.status);
  return status >= 400 && status !== 130;
}

export function docGenFailureMessage(response: any, fallback?: string): string {
  return String(
    (response && (response.message || response.error)) ||
      fallback ||
      "Document generation failed.",
  );
}

export function isDocGenLockError(message: string): boolean {
  const m = (message || "").toLowerCase();
  return (
    m.includes("is locked") ||
    m.includes("resource temporarily unavailable") ||
    m.includes("unlink(")
  );
}

export function formatDocGenPollHttpError(
  status: number,
  bodyText?: string,
): string {
  let extra = "";
  const raw = (bodyText || "").trim();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      extra = String(parsed.error || parsed.message || "").trim();
    } catch {
      extra = raw
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 240);
    }
  }
  return extra
    ? `Cannot open the generated document (HTTP ${status}). ${extra}`
    : `Cannot open the generated document (HTTP ${status}). Click Retry.`;
}

export function pollGeneratedDocx(args: {
  url: string;
  isCancelled: () => boolean;
  onReady: () => void;
  onFailure: (message: string) => void;
  schedule: (fn: () => void, ms: number) => void;
}): void {
  const started = Date.now();
  const maxMs = DOC_GEN_TIMEOUT_SEC * 1000;

  const failIfWorkerDownOr = async (fallback: string) => {
    if (args.isCancelled()) {
      return;
    }
    const running = await isDocGenWorkerRunning();
    if (args.isCancelled()) {
      return;
    }
    args.onFailure(
      running === false ? DOC_GEN_WORKER_DOWN_MESSAGE : fallback,
    );
  };

  const tick = () => {
    if (args.isCancelled()) {
      return;
    }
    if (Date.now() - started >= maxMs) {
      failIfWorkerDownOr(DOC_GEN_TIMEOUT_MESSAGE);
      return;
    }
    if (!args.url) {
      args.onFailure(
        "Document generation did not return a file path. Click Retry.",
      );
      return;
    }
    fetch(args.url)
      .then(async (response) => {
        if (args.isCancelled()) {
          return;
        }
        if (response.ok && response.status === 200) {
          args.onReady();
          return;
        }
        if (response.status === 404) {
          args.schedule(tick, DOC_GEN_FILE_POLL_MS);
          return;
        }
        const text = await response.text().catch(() => "");
        args.onFailure(formatDocGenPollHttpError(response.status, text));
      })
      .catch(() => {
        if (args.isCancelled()) {
          return;
        }
        if (Date.now() - started >= maxMs) {
          failIfWorkerDownOr(DOC_GEN_TIMEOUT_MESSAGE);
          return;
        }
        args.schedule(tick, DOC_GEN_FILE_POLL_MS);
      });
  };

  tick();
}

export function releaseDocGenClickFreeze() {
  try {
    const jq = (window as any).$;
    if (jq) {
      jq("#root").off(".docgenfreeze");
      jq("#root").unbind("click mousedown dblclick");
      jq(".sw-theme-arrows .content").off(".docgenfreeze");
    }
  } catch (e) {
    // ignore
  }
}
